import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ConfirmDialog, Field, PageHeader, RowMenu, useToast } from "../../components/ui";
import { ErrorState } from "../../components/States";
import { setApiOrg, setApiProject } from "../../api";
import { useAuth } from "../../auth/store";
import { formatDateTime } from "../../lib/format";
import "./staff.css";
import { staffApi, staffKeys } from "./api";
import { BillingStatusBadge, PlanBadge, formatDate } from "./customers";
import {
  AccessDialog,
  AccessTab,
  NetworksTab,
  ActivityTab,
  CreditDialog,
  CreditsTab,
  FeatureGrantDialog,
  InviteDialog,
  MembersTab,
  OverviewTab,
  SettingsTab,
  TABS,
  BillingTab,
  UsageTab,
  type CustomerTab,
} from "./customerTabs";

type DialogKind = "credit-add" | "credit-reduce" | "feature-grant" | "invite" | "access" | null;
type DeleteStep = "confirm" | "type" | null;

/**
 * One customer: who they are, what they pay for and use, who works there, the
 * credits they hold, our access to them, and everything the platform team has
 * done to the account. Tabs live in the URL so a link lands on the right one.
 */
export function StaffOrgPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const search = useSearch({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const toast = useToast();
  const owner = useAuth().me?.user.staffRole === "owner";
  const tab: CustomerTab = TABS.some((t) => t.id === search.tab) ? (search.tab as CustomerTab) : "overview";
  const setTab = (t: CustomerTab) => void navigate({ to: "/staff/orgs/$org", params: { org: orgId }, search: t === "overview" ? {} : { tab: t }, replace: true });
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [deleteStep, setDeleteStep] = useState<DeleteStep>(null);
  const [typedName, setTypedName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const close = () => setDialog(null);

  const detail = useQuery({ queryKey: staffKeys.org(orgId), queryFn: () => staffApi.org(orgId) });
  const d = detail.data;
  const grant = d?.grants.find((g) => g.status === "active");
  const waiting = d?.grants.filter((g) => g.status === "pending").length ?? 0;
  const openSessions = () => {
    if (!d) return;
    setApiOrg(orgId);
    if (d.projects[0]) setApiProject(d.projects[0].id);
    void navigate({ to: "/staff/orgs/$org/sessions", params: { org: orgId } });
  };

  return (
    <div className="cust-page">
      <PageHeader
        back={
          <Link to="/staff" className="btn ghost cust-back" aria-label="Back to customers">
            ←
          </Link>
        }
        title={
          d ? (
            <span className="cust-title">
              {d.org.name}
              <PlanBadge plan={d.plan} />
              {(d.billing.status !== "none" || d.plan !== "free") && <BillingStatusBadge status={d.billing.status} />}
            </span>
          ) : detail.isError ? (
            "Customer"
          ) : (
            <span className="skeleton" style={{ width: 220, height: 22 }}>
              loading
            </span>
          )
        }
        subtitle={d ? `${d.org.slug} · customer since ${formatDate(d.org.createdAt)}${d.org.profile?.contactEmail ? ` · ${d.org.profile.contactEmail}` : ""}` : undefined}
        actions={
          d ? (
            <>
              <button type="button" className="btn primary" onClick={() => setDialog("credit-add")}>
                Add credits
              </button>
              <RowMenu label={`More actions for ${d.org.name}`}>
                <button type="button" role="menuitem" onClick={() => setDialog("feature-grant")}>
                  Grant feature…
                </button>
                <button type="button" role="menuitem" onClick={() => setDialog("credit-reduce")}>
                  Reduce credits…
                </button>
                <button type="button" role="menuitem" onClick={() => setDialog("invite")}>
                  Invite member…
                </button>
                <button type="button" role="menuitem" onClick={() => setDialog("access")} disabled={d.grants.some((g) => g.status === "pending" || g.status === "active")}>
                  Request support access…
                </button>
                <button type="button" role="menuitem" onClick={openSessions} disabled={!grant}>
                  {grant ? "Open their sessions" : "Open their sessions (needs access)"}
                </button>
                {owner && (
                  <button
                    type="button"
                    role="menuitem"
                    className="danger"
                    onClick={() => {
                      setTypedName("");
                      setDeleteStep("confirm");
                    }}
                  >
                    Delete organization
                  </button>
                )}
              </RowMenu>
            </>
          ) : undefined
        }
      >
        <nav className="section-tabs cust-tabs" aria-label="Customer sections">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={t.id === tab ? "active" : undefined} aria-current={t.id === tab ? "page" : undefined} onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === "access" && waiting > 0 && <span className="nav-count" aria-label={`${waiting} waiting`}>{waiting}</span>}
            </button>
          ))}
        </nav>
      </PageHeader>

      {grant && (
        <div className="banner info cust-access-banner" role="status">
          <span>
            Support access is active until <strong>{formatDateTime(grant.expiresAt)}</strong>.
          </span>
          <button type="button" className="btn" onClick={openSessions}>
            Open their sessions
          </button>
        </div>
      )}

      {detail.isPending ? (
        <CustomerSkeleton />
      ) : detail.isError ? (
        <ErrorState error={detail.error} retry={() => void detail.refetch()} />
      ) : d ? (
        <>
          {tab === "overview" && <OverviewTab d={d} />}
          {tab === "usage" && <UsageTab d={d} />}
          {tab === "billing" && <BillingTab d={d} />}
          {tab === "members" && <MembersTab d={d} onInvite={() => setDialog("invite")} />}
          {tab === "credits" && <CreditsTab d={d} onAdd={() => setDialog("credit-add")} onReduce={() => setDialog("credit-reduce")} onGrantFeature={() => setDialog("feature-grant")} />}
          {tab === "access" && <AccessTab d={d} onRequest={() => setDialog("access")} />}
          {tab === "networks" && <NetworksTab d={d} />}
          {tab === "activity" && <ActivityTab d={d} />}
          {tab === "settings" && <SettingsTab d={d} />}

          <CreditDialog d={d} mode={dialog === "credit-reduce" ? "reduce" : "add"} open={dialog === "credit-add" || dialog === "credit-reduce"} onClose={close} />
          <FeatureGrantDialog d={d} open={dialog === "feature-grant"} onClose={close} />
          <InviteDialog d={d} open={dialog === "invite"} onClose={close} />
          <AccessDialog d={d} open={dialog === "access"} onClose={close} />
          <ConfirmDialog
            open={deleteStep === "confirm"}
            danger
            title="Delete organization"
            confirmLabel="Continue"
            typeToConfirm={false}
            onConfirm={() => setDeleteStep("type")}
            onClose={() => setDeleteStep(null)}
          >
            This permanently deletes {d.org.name}, including its sessions, files, and billing. People who only belong to this organization are removed too.
          </ConfirmDialog>
          <ConfirmDialog
            open={deleteStep === "type"}
            danger
            title={`Delete ${d.org.name}`}
            confirmLabel={deleting ? "Deleting…" : "Delete organization"}
            busy={deleting}
            typeToConfirm={false}
            disabled={typedName.trim() !== d.org.name.trim()}
            onConfirm={() => {
              setDeleting(true);
              void staffApi
                .deleteOrg(orgId, typedName.trim())
                .then(() => {
                  toast.push("success", `${d.org.name} was deleted.`);
                  void navigate({ to: "/staff" });
                })
                .catch((cause: Error) => {
                  toast.push("error", cause.message);
                  setDeleting(false);
                });
            }}
            onClose={() => {
              if (deleting) return;
              setDeleteStep(null);
            }}
          >
            <p>Type {d.org.name} to confirm. Everything recorded for this organization will be deleted.</p>
            <Field label="Organization name">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  value={typedName}
                  autoFocus
                  autoComplete="off"
                  onChange={(event) => setTypedName(event.target.value)}
                />
              )}
            </Field>
          </ConfirmDialog>
        </>
      ) : null}
    </div>
  );
}

function CustomerSkeleton() {
  return (
    <div aria-busy="true">
      <div className="cust-stats">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="card cust-stat">
            <span className="skeleton" style={{ width: 70, height: 11 }}>loading</span>
            <span className="skeleton" style={{ width: 90, height: 22 }}>loading</span>
          </div>
        ))}
      </div>
      <div className="cust-grid">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="card cust-card">
            <span className="skeleton" style={{ width: 100, height: 14, marginBottom: 12 }}>loading</span>
            {Array.from({ length: 4 }).map((__, j) => (
              <span key={j} className="skeleton" style={{ width: `${55 + ((j * 19) % 40)}%`, height: 12, marginBottom: 8, display: "block" }}>
                loading
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
